import { Button, Col, Div, Row, Text } from "atomize"
import { haptic } from "./utils/haptics.utils"
import { theme } from "./settings/config"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { modalContext } from "./providers/modal.provider"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCalendarDay,
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import LessonsList from "./LessonsList"
import Container from "./atoms/Container"
import { motion, useTransform } from "framer-motion"
import DateInput from "./inputs/DateInput"
import { profileContext } from "./providers/profile.provider"
import { lessonsContext } from "./providers/lessons.provider"
import { schoolContext } from "./providers/school.provider"
import { canAssignLessonTo, getDayOfWeek, teachesInSchool } from "./utils/func.utils"
import { isSmartphone } from "./atoms/SmartphoneView"
import { ABOVE_PHONE_NAV } from "./atoms/phoneBars"
import { isDesktopLike } from "./atoms/DesktopLikeView"
import { faCalendar } from "@fortawesome/free-regular-svg-icons"
import PhoneLessons from "./week/PhoneLessons"
import WeekGrid from "./week/WeekGrid"
import { getWeekRangeLabel } from "./utils/week.utils"
import HoldArrowButton from "./buttons/HoldArrowButton"
import NewLessonButton from "./buttons/NewLessonButton"
import NewLessonMenu from "./buttons/NewLessonMenu"
import useGestureHint, { HINTS } from "./week/useGestureHint"
import usePinchToSwitchView, { pinchProgress, pinchTarget } from "./week/usePinchToSwitchView"
import PhoneCoachDropdown from "./profile/PhoneCoachDropdown"

function LessonsContainer() {
	const Modal = useContext(modalContext)
	const { coach, displayedCoachID } = useContext(profileContext)
	const { getSelectedSchool, selectedSchool } = useContext(schoolContext)
	const {
		lessons,
		selectedDate,
		setSelectedDate,
		lessonsView,
		setLessonsView,
		setLessonToOpen,
		viewProgress,
		matchesEnabled,
	} = useContext(lessonsContext)

	// In settimana le frecce spostano di una settimana, e in mezzo c'è quale
	// settimana è; il resto della barra non cambia.
	const isWeek = lessonsView === "week"
	const moveBy = (direction, step = isWeek ? { weeks: 1 } : { days: 1 }) =>
		setSelectedDate(
			DateTime.fromISO(selectedDate)
				[direction > 0 ? "plus" : "minus"](step)
				.toISO()
				.split("T")[0],
		)

	// Nel giorno, sul telefono, tenere premuta una freccia salta di una
	// settimana: il computer ha i suoi due bottoni, e in settimana la freccia
	// fa già quel salto. Il gesto non si vede, e un avviso lo dice le prime
	// volte.
	const canHoldToJump = isSmartphone && !isWeek
	const { showHint: showJumpHint, markUsed: markJumpUsed } = useGestureHint(
		HINTS.weekJump,
		canHoldToJump,
	)
	const jumpWeek = direction => {
		markJumpUsed()
		moveBy(direction, { weeks: 1 })
	}

	// Sul telefono si passa dal giorno alla settimana stringendo due dita, e
	// si torna allargandole: su un giorno della settimana si apre quello. Con
	// un modale aperto le dita sono sue. Anche questo gesto non si vede: il
	// suo avviso aspetta che quello delle frecce abbia finito.
	// Servono due dita vere: il telefono simulato del computer (F12) ne ha
	// una sola, e lì l'avviso chiederebbe un gesto impossibile.
	const canPinch = isSmartphone && (navigator.maxTouchPoints ?? 0) > 1
	const { showHint: showPinchHint, markUsed: markPinchUsed } = useGestureHint(
		HINTS.pinch,
		canPinch && !showJumpHint,
	)
	// Il "+" chiede "Lezione o Gioco io?" (src/utils/match.utils.js), se non
	// lo si è spento nel profilo; tenuto premuto va dritto alla lezione.
	//
	// Chi qui non insegna lezioni sue non ne ha: il "+" c'è solo se quella
	// nuova può andare al collega che sta guardando, perché ne tiene le
	// lezioni. Se no non c'è proprio — un bottone spento che non si accende
	// mai è solo un posto occupato.
	const teachesHere = teachesInSchool(coach, selectedSchool)
	const canAddHere =
		teachesHere ||
		(displayedCoachID !== coach?.coachID &&
			canAssignLessonTo(coach, selectedSchool, displayedCoachID))
	// finché circolo e maestro non ci sono il "+" gira, al suo posto
	const isLoadingNew = !(getSelectedSchool() && coach)
	const [isNewMenuOpen, setNewMenuOpen] = useState(false)
	const openNew = modal => {
		setNewMenuOpen(false)
		if (getSelectedSchool()) Modal.open(modal, selectedDate)
	}

	// Il passaggio segue le dita (vedi week/viewMorph): da dove parte, e alla
	// fine si sceglie la vista dove si è arrivati, o si torna indietro.
	const pinchFrom = useRef(null)
	usePinchToSwitchView({
		enabled: canPinch && !Modal.isOpen,
		onStart: ({ x, y }) => {
			viewProgress.stop()
			pinchFrom.current = viewProgress.get()
			// una lezione da aprire rimasta in sospeso non deve saltare fuori
			setLessonToOpen(null)
			// allargando le dita su un giorno della settimana si apre quello
			if (pinchFrom.current >= 0.5) {
				const day = document.elementFromPoint?.(x, y)?.closest("[data-week-day]")
				const date = day?.getAttribute("data-week-day")
				if (date && date !== selectedDate) setSelectedDate(date)
			}
		},
		onChange: scale => viewProgress.set(pinchProgress(pinchFrom.current, scale)),
		onEnd: () => {
			const from = pinchFrom.current
			const target = pinchTarget(from, viewProgress.get())
			setLessonsView(target === 1 ? "week" : "day")
			if (target !== Math.round(from)) {
				haptic("tick")
				markPinchUsed()
			}
		},
	})

	// In basso il giorno o la settimana: sul telefono uno sfuma nell'altro
	// insieme al passaggio
	const dayLabelOpacity = useTransform(viewProgress, [0, 0.5], [1, 0])
	const weekLabelOpacity = useTransform(viewProgress, [0.5, 1], [0, 1])

	return (
		<>
			<Container>
				{isSmartphone && <PhoneLessons />}
				{isWeek && isDesktopLike && <WeekGrid />}
				{!isWeek &&
					isDesktopLike &&
					(getSelectedSchool() &&
					lessons[selectedSchool]?.[selectedDate] &&
					Object.keys(lessons[selectedSchool][selectedDate]).length > 0 ? (
						<Row>
							{Object.keys(lessons[selectedSchool][selectedDate])
								.sort((a, b) => {
									// chi non ha più un nome — un maestro che ha chiuso
									// l'account — va in fondo, non davanti a tutti
									const nameOf = coachID =>
										getSelectedSchool().coaches[
											coachID
										]?.coachName?.toLowerCase() ?? "\uffff"
									return nameOf(a).localeCompare(nameOf(b))
								})
								.map((lessonsCoachID, i) => {
									if (
										lessons[selectedSchool]?.[selectedDate]?.[
											lessonsCoachID
										]?.filter(lesson => lesson.schoolID === selectedSchool)
											.length > 0
									)
										return (
											<Col key={i}>
												<LessonsList
													selectedDate={selectedDate}
													lessons={
														lessons[selectedSchool]?.[selectedDate]?.[
															lessonsCoachID
														]
													}
													lessonsCoach={
														getSelectedSchool().coaches[lessonsCoachID]
													}
													lessonsCoachID={lessonsCoachID}
												/>
											</Col>
										)
								})}
						</Row>
					) : (
						<Text textSize="subheader" textAlign="center" m="2.5rem auto">
							RELAX, non ci sono lezioni in questo giorno! 😌
						</Text>
					))}
			</Container>
			<Div d="flex" justify="center">
				<motion.div
					style={
						isSmartphone
							? {
									position: "fixed",
									bottom: ABOVE_PHONE_NAV,
									transform: "translateX(-50%)",
									width: "calc(100% - 2rem)",
									zIndex: "1",
								}
							: {
									position: "fixed",
									// in settimana mancano i due bottoni da una settimana:
									// la barra si stringe, e i bottoni restano larghi uguali
									width: isWeek ? "24rem" : "35rem",
									// sul computer sotto non c'è la barra di navigazione: la
									// barra delle lezioni scende, e lascia spazio alla settimana
									bottom: "1.25rem",
									zIndex: "1",
								}
					}
					initial={{ y: 20 }}
					animate={{ y: 0 }}
					exit={{ y: 20 }}>
					<PhoneCoachDropdown />
					{(showJumpHint || showPinchHint) && (
						<Text
							textSize="caption"
							textAlign="center"
							textColor={theme.colors.text_light}
							m={{ x: "auto", b: "0.4rem" }}
							p="0.2rem 0.75rem"
							w="fit-content"
							rounded="12px"
							bg={theme.colors.surface}>
							{showJumpHint
								? "Tieni premuta una freccia per saltare di una settimana"
								: isWeek
								  ? "Allarga due dita su un giorno per aprirlo"
								  : "Stringi due dita sulle lezioni per vedere la settimana"}
						</Text>
					)}
					<Row bg={theme.colors.primary_light} rounded="20px" p="0.5rem">
						<Col size={isSmartphone && "2"} style={{ padding: "0 0.25rem" }}>
							<motion.div whileTap={{ scale: 0.9 }}>
								<Button
									w="3.25rem"
									h="3.25rem"
									m="auto"
									bg={theme.colors.surface}
									shadow={theme.shadows.accent}
									rounded={theme.rounded["20px"]}
									onClick={() =>
										setSelectedDate(DateTime.now().toISO().split("T")[0])
									}>
									<FontAwesomeIcon
										icon={faCalendar}
										color={theme.colors.gunmetal}
										size="lg"
									/>
								</Button>
							</motion.div>
						</Col>
						{isDesktopLike && !isWeek && (
							<Col style={{ padding: "0 0.25rem" }}>
								<motion.div whileTap={{ scale: 0.9 }}>
									<Button
										w="3.25rem"
										h="3.25rem"
										m="auto"
										bg={theme.colors.surface}
										shadow={theme.shadows.accent}
										rounded={theme.rounded["20px"]}
										onClick={() =>
											setSelectedDate(
												DateTime.fromISO(selectedDate)
													.minus({ weeks: 1 })
													.toISO()
													.split("T")[0],
											)
										}>
										<FontAwesomeIcon
											icon={faChevronLeft}
											color={theme.colors.gunmetal}
										/>
										<FontAwesomeIcon
											icon={faChevronLeft}
											color={theme.colors.gunmetal}
										/>
									</Button>
								</motion.div>
							</Col>
						)}
						<Col size={isSmartphone && "2"} style={{ padding: "0 0.25rem" }}>
							<HoldArrowButton
								direction={-1}
								title={isWeek ? "Settimana prima" : "Giorno prima"}
								onTap={() => moveBy(-1)}
								onHold={canHoldToJump ? () => jumpWeek(-1) : undefined}
							/>
						</Col>
						<Col
							size="4"
							pos="relative"
							style={{ padding: "0 0.25rem" }}
							onClick={() => {
								const input = document.getElementById("date_input")
								if (input.showPicker) input.showPicker()
							}}>
							<>
								<Text
									d="flex"
									pos="relative"
									justify="center"
									align="center"
									h="3.25rem"
									textSize={isWeek && !isSmartphone ? "body" : "subheader"}
									textAlign="center"
									textColor={theme.colors.gunmetal}
									zIndex="1"
									style={{ whiteSpace: "nowrap" }}>
									{isSmartphone ? (
										<span style={{ display: "grid", justifyItems: "center" }}>
											<motion.span
												style={{ gridArea: "1 / 1", opacity: dayLabelOpacity }}>
												{getDayOfWeek(selectedDate)}
											</motion.span>
											<motion.span
												style={{
													gridArea: "1 / 1",
													opacity: weekLabelOpacity,
													fontSize: "1rem",
												}}>
												{getWeekRangeLabel(selectedDate)}
											</motion.span>
										</span>
									) : isWeek ? (
										getWeekRangeLabel(selectedDate)
									) : (
										getDayOfWeek(selectedDate)
									)}
								</Text>
								<Div pos="absolute" top="0" left="0" w="100%" h="100%" zIndex="2">
									<DateInput
										id="date_input"
										w="100%"
										h="100%"
										opacity="0"
										m="0"
										shadow={theme.shadows.accent}
										value={selectedDate}
										handleChange={setSelectedDate}
									/>
								</Div>
							</>
						</Col>
						<Col size={isSmartphone && "2"} style={{ padding: "0 0.25rem" }}>
							<HoldArrowButton
								direction={1}
								title={isWeek ? "Settimana dopo" : "Giorno dopo"}
								onTap={() => moveBy(1)}
								onHold={canHoldToJump ? () => jumpWeek(1) : undefined}
							/>
						</Col>
						{isDesktopLike && !isWeek && (
							<Col style={{ padding: "0 0.25rem" }}>
								<motion.div whileTap={{ scale: 0.9 }}>
									<Button
										w="3.25rem"
										h="3.25rem"
										m="auto"
										bg={theme.colors.surface}
										shadow={theme.shadows.accent}
										rounded={theme.rounded["20px"]}
										onClick={() =>
											setSelectedDate(
												DateTime.fromISO(selectedDate)
													.plus({ weeks: 1 })
													.toISO()
													.split("T")[0],
											)
										}>
										<FontAwesomeIcon
											icon={faChevronRight}
											color={theme.colors.gunmetal}
										/>
										<FontAwesomeIcon
											icon={faChevronRight}
											color={theme.colors.gunmetal}
										/>
									</Button>
								</motion.div>
							</Col>
						)}
						{(isLoadingNew || canAddHere) && (
							<Col size={isSmartphone && "2"} style={{ padding: "0 0.25rem" }}>
								<Div pos="relative">
									{isNewMenuOpen && (
										<NewLessonMenu
											onLesson={() => openNew("newLesson")}
											onMatch={() => openNew("newMatch")}
											onClose={() => setNewMenuOpen(false)}
										/>
									)}
									<NewLessonButton
										isLoading={isLoadingNew}
										disabled={!canAddHere}
										hasMenu={matchesEnabled}
										isMenuOpen={isNewMenuOpen}
										onTap={() => {
											haptic("tick")
											if (matchesEnabled) setNewMenuOpen(!isNewMenuOpen)
											else openNew("newLesson")
										}}
										onHold={() => openNew("newLesson")}
									/>
								</Div>
							</Col>
						)}
					</Row>
				</motion.div>
			</Div>
		</>
	)
}

export default LessonsContainer
